import {
  IDocumentAssemblerTemplate,
  IDocumentAssemblerTemplateVersionDetail,
  TDocumentAssemblerVersionRefusal,
} from '@commenda/api';
import { Alert, Button, ScrollArea, useToggle } from '@commenda/kakashi';
import { ConfirmationModal } from '../molecules/modals/ConfirmationModal';

import type { DocumentAssemblerVersionLifecycle } from './document-assembler.types';

interface DocumentAssemblerVersionLifecycleCardProps {
  template: IDocumentAssemblerTemplate | undefined;
  version: IDocumentAssemblerTemplateVersionDetail;
  lifecycle: DocumentAssemblerVersionLifecycle;
  isActionPending: boolean;
  refusal: TDocumentAssemblerVersionRefusal | null;
  onRefusalDismiss: () => void;
  onVersionPublish: () => Promise<boolean>;
  onVersionActivate: () => Promise<boolean>;
  onVersionArchive: () => Promise<boolean>;
}

export const DocumentAssemblerVersionLifecycleCard = ({
  template,
  version,
  lifecycle,
  isActionPending,
  refusal,
  onRefusalDismiss,
  onVersionPublish,
  onVersionActivate,
  onVersionArchive,
}: DocumentAssemblerVersionLifecycleCardProps) => {
  const activateConfirmModalVisibility = useToggle(false);
  const archiveConfirmModalVisibility = useToggle(false);

  const currentActiveVersion = template?.activeVersion;

  return (
    <>
      <div className="flex flex-col gap-lg rounded-xl border border-border-primary p-xl">
        <div className="flex flex-col gap-xs">
          <h3 className="txt-medium-medium text-text-primary">
            Version status
          </h3>
          <p className="txt-extra-small-regular text-text-secondary">
            {lifecycle.stateSentence}
          </p>
        </div>

        {/* Inline, never a toast: a toast saying "cannot activate" sends the author to Slack. */}
        {refusal ? (
          <Alert
            variant="destructive"
            title={refusal.message}
            onClose={onRefusalDismiss}
            description={
              refusal.kind === 'NOT_USABLE' ? (
                <ScrollArea className="max-h-64">
                  <ul className="flex list-disc flex-col gap-xs pl-xl">
                    {refusal.errors.map((error, index) => (
                      <li
                        key={`${error.code}-${index}`}
                        className="txt-extra-small-regular"
                      >
                        {error.message}
                      </li>
                    ))}
                  </ul>
                </ScrollArea>
              ) : undefined
            }
          />
        ) : null}

        <div className="flex flex-wrap items-center gap-md">
          {lifecycle.canPublish ? (
            <Button
              variant="primary"
              size="sm"
              disabled={isActionPending}
              onClick={() => {
                onVersionPublish().catch(() => undefined);
              }}
            >
              Publish
            </Button>
          ) : null}

          {lifecycle.canActivate ? (
            <Button
              variant="primary"
              size="sm"
              disabled={isActionPending}
              onClick={activateConfirmModalVisibility.on}
            >
              Mark as active
            </Button>
          ) : null}

          {lifecycle.canArchive ? (
            <Button
              variant="destructive-secondary"
              size="sm"
              disabled={isActionPending}
              onClick={archiveConfirmModalVisibility.on}
            >
              Archive version
            </Button>
          ) : null}
        </div>

        {lifecycle.publishBlockedReason ? (
          <p className="txt-extra-small-regular text-text-secondary">
            {lifecycle.publishBlockedReason}
          </p>
        ) : null}
        {lifecycle.archiveBlockedReason ? (
          <p className="txt-extra-small-regular text-text-secondary">
            {lifecycle.archiveBlockedReason}
          </p>
        ) : null}
      </div>

      <ConfirmationModal
        isOpen={activateConfirmModalVisibility.value}
        onClose={activateConfirmModalVisibility.off}
        onConfirm={onVersionActivate}
        title={`Mark version ${version.version} as active`}
        message={
          currentActiveVersion
            ? `New documents will be produced from version ${version.version}. Version ${currentActiveVersion.version} stays published and is not changed.`
            : `New documents will be produced from version ${version.version}.`
        }
        confirmButtonText="Mark as active"
      />

      <ConfirmationModal
        isOpen={archiveConfirmModalVisibility.value}
        onClose={archiveConfirmModalVisibility.off}
        onConfirm={onVersionArchive}
        tone="destructive"
        title={`Archive version ${version.version}`}
        message="Archiving is not deleting — the record of every document this version produced is kept. It can no longer be marked active, and it cannot be un-archived."
        confirmButtonText="Archive"
      />
    </>
  );
};
